import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, Star, Sparkles } from "lucide-react";
import { Reveal, RevealLines } from "./Reveal";
import { BRAND } from "../../lib/brand";

const IMG = "/images/signature.png";

const pillars = [
  {
    n: "I",
    t: "Maderothérapie sculptante",
    b: "Ouverture du soin par le geste ferme. Les tissus s'éveillent, les contours se redessinent.",
  },
  {
    n: "II",
    t: "Drainage lymphatique",
    b: "Enchaînement immédiat pour évacuer ce que le sculpt' a mobilisé. La circulation lymphatique s'ouvre en profondeur.",
  },
  {
    n: "III",
    t: "Optimisation & prolongation",
    b: "Manœuvres finales et gestuelle sur-mesure pour ancrer et prolonger les bienfaits du soin signature.",
  },
];

function SignatureTitle() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });
  return (
    <span className="reveal-line block" ref={ref}>
      <motion.span
        initial={{ y: "110%" }}
        animate={inView ? { y: "0%" } : { y: "110%" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif text-olive text-3xl sm:text-4xl md:text-6xl lg:text-[5.25rem] leading-[1] tracking-tighter signature-glow whitespace-nowrap"
      >
        Rituel signature{" "}
        <span className="italic text-gold signature-glow-gold">
          Corps &amp; Sens.
        </span>
      </motion.span>
    </span>
  );
}

// Small animated gold sparkle
function GoldSparkle({ className = "", delay = 0 }) {
  return (
    <motion.span
      className={`absolute pointer-events-none ${className}`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0, 1, 0.7, 1], scale: [0.5, 1.2, 0.9, 1.1] }}
      transition={{
        duration: 4.5,
        delay,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      }}
    >
      <Sparkles className="h-3 w-3 md:h-3.5 md:w-3.5 text-gold-bright" strokeWidth={1.4} />
    </motion.span>
  );
}

export default function Signature() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const yDetail = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section
      id="signature"
      ref={ref}
      className="relative bg-cream-deep overflow-hidden"
      data-testid="signature-section"
    >
      {/* Animated gold hairline top - shimmering */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent origin-left"
      />

      {/* Floating gold sparkles */}
      <GoldSparkle className="top-[15%] left-[8%]" delay={0} />
      <GoldSparkle className="top-[30%] right-[12%]" delay={1.2} />
      <GoldSparkle className="bottom-[25%] left-[20%]" delay={2.4} />
      <GoldSparkle className="top-[55%] right-[6%]" delay={0.6} />
      <GoldSparkle className="bottom-[15%] right-[25%]" delay={1.8} />

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10 py-20 md:py-28">
        {/* Chapter tag + premium badge */}
        <div className="mb-8 md:mb-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-4">
              <span className="chapter-number text-3xl md:text-4xl">03</span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-olive/60">
                Le Rituel Signature
              </span>
              <motion.span
                initial={{ opacity: 0, scale: 0.5, rotate: -30 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex relative"
                data-testid="signature-star"
                aria-label="Soin premium"
              >
                <Star className="h-4 w-4 md:h-5 md:w-5 fill-gold text-gold" />
                <motion.span
                  className="absolute inset-0 rounded-full"
                  animate={{
                    boxShadow: [
                      "0 0 0 0 rgba(184,148,79,0.5)",
                      "0 0 0 12px rgba(184,148,79,0)",
                    ],
                  }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                />
              </motion.span>

              {/* Premium badge */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="ml-auto md:ml-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 bg-gold/5 backdrop-blur-sm"
              >
                <motion.span
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 2.2, repeat: Infinity }}
                  className="h-1.5 w-1.5 rounded-full bg-gold-bright"
                />
                <span className="text-gold text-[10px] uppercase tracking-[0.28em] font-medium">
                  Exclusivité Corps &amp; Sens
                </span>
              </motion.div>
            </div>
          </Reveal>
        </div>

        {/* Title on one line */}
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="md:col-span-12">
            <SignatureTitle />
          </div>
          <div className="md:col-span-12">
            <Reveal delay={0.35}>
              <p className="mt-6 md:mt-8 text-olive/75 text-[16px] md:text-[17px] leading-[1.7] font-light max-w-[560px]">
                L'expérience complète — pensée comme une seule séance
                architecturale. La maderothérapie ouvre, le drainage relance,
                l'optimisation ancre. Un soin complet et personnalisé.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Imagery + pillars — 2 columns now */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-start">
          {/* Left image with gold frame accent */}
          <div className="md:col-span-5 relative">
            <motion.div
              style={{ y: yImg }}
              className="relative aspect-[4/5] w-full max-w-[500px]"
            >
              {/* Gold decorative corners */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.3 }}
                className="absolute -top-3 -right-3 h-16 w-16 border-t-2 border-r-2 border-gold z-10"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.5 }}
                className="absolute -bottom-3 -left-3 h-16 w-16 border-b-2 border-l-2 border-gold z-10"
              />

              <div className="absolute inset-0 frame-clip-alt overflow-hidden">
                <img
                  src={IMG}
                  alt="Rituel signature Corps & Sens"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="grain absolute inset-0 opacity-20 mix-blend-multiply pointer-events-none" />
              </div>
            </motion.div>
          </div>

          {/* Right pillars — now larger */}
          <div className="md:col-span-7">
            <div className="space-y-10">
              {pillars.map((p, i) => (
                <Reveal key={i} delay={0.15 + i * 0.1}>
                  <div className="group grid grid-cols-[auto_1fr] gap-6 md:gap-10 border-t border-olive/25 pt-8 transition-colors duration-700 hover:border-gold">
                    <div className="relative">
                      <span className="chapter-number text-4xl md:text-5xl">
                        {p.n}.
                      </span>
                      <motion.span
                        className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-gold-bright opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        animate={{ scale: [1, 1.4, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity }}
                      />
                    </div>
                    <div>
                      <h4 className="font-serif text-2xl md:text-[30px] text-olive tracking-tight leading-tight">
                        {p.t}
                      </h4>
                      <p className="mt-3 text-olive/70 text-[15px] leading-[1.75] font-light max-w-[520px]">
                        {p.b}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-16 md:mt-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Reveal>
              <p className="font-serif italic text-gold text-xl md:text-3xl lg:text-4xl leading-tight whitespace-nowrap">
                « Le soin que l'on n'oublie pas. »
              </p>
              <p className="mt-4 text-olive/60 text-[12px] uppercase tracking-[0.28em]">
                Sur réservation uniquement
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Reveal delay={0.2}>
              <a
                href={BRAND.phoneHref}
                data-testid="cta-signature-reserve"
                className="btn-gold inline-flex items-center gap-3 px-9 py-5 text-[12px] uppercase tracking-[0.24em] rounded-full"
              >
                <Phone className="h-4 w-4" strokeWidth={1.6} />
                Réserver le rituel
              </a>
            </Reveal>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent origin-right"
      />
    </section>
  );
}
